// packages/core/src/spr-answer.ts
//
// Moved here from src/lib/test-engine/spr-answer.ts (2026-10-15) so the
// mobile app grades grid-ins with the SAME code as the website; the web path
// now re-exports this file, so there is still exactly one implementation.
//
// THE single place SPR (student-produced response / grid-in) equivalence is
// decided. Nothing else in the codebase may compare a typed answer to an
// answer key by hand.
//
// Why this file is now the only implementation
// --------------------------------------------
// There used to be TWO SPR graders that disagreed:
//
//   • this module's `compareSprAnswer` — naive `parseFloat`, so "5abc" parsed
//     as 5 and graded CORRECT, and it had no truncate/round rule so ".333"
//     was marked WRONG for 1/3;
//   • `gradeSpr` in lib/sat/scoring.ts — strict regex, relative epsilon, and
//     the real College Board truncate-or-round rule.
//
// …and, worse, five grading paths used NEITHER and compared raw strings, so
// "1.8" !== "1.80" marked a correct answer wrong (the notebooks/$2.40 bug).
// The duplication was the root cause, not any single comparison.
//
// The strict implementation won. It lives here; lib/sat/scoring.ts re-exports
// it so existing importers are unaffected, and `compareSprAnswer` survives as
// a thin alias so the lesson MiniCheck keeps working — but every caller now
// runs the SAME code.

/**
 * Parse an SPR entry into a number. Accepts integers, decimals (leading dot
 * allowed), and simple fractions `a/b`. Returns null for anything else.
 *
 * Deliberately strict: the whole string must be numeric. A lenient parse
 * (`parseFloat`) silently accepts "5abc" as 5, which graded junk as correct.
 */
export function parseSprNumber(raw: string | null | undefined): number | null {
  if (raw == null) return null
  const t = raw.trim()
  if (t === '') return null
  const frac = t.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*\/\s*([+-]?(?:\d+(?:\.\d+)?|\.\d+))$/)
  if (frac) {
    const num = Number(frac[1])
    const den = Number(frac[2])
    if (Number.isNaN(num) || Number.isNaN(den) || den === 0) return null
    return num / den
  }
  if (/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(t)) {
    const n = Number(t)
    return Number.isNaN(n) ? null : n
  }
  return null
}

/** Count decimal places in a plain decimal string (0 for fractions/integers). */
function sprDecimalPlaces(raw: string): number {
  const m = raw.trim().match(/^[+-]?\d*\.(\d+)$/)
  return m ? m[1].length : 0
}

/**
 * Characters an SPR entry may use, not counting a leading minus — the grid
 * itself (SPR_MAX_LEN_POSITIVE / _NEGATIVE in lib/sat/answer-validation) is
 * 5 for a positive answer and 6 for a negative one, i.e. the sign is free.
 */
const SPR_VALUE_CHARS = 5

/**
 * How many decimal places the grid can actually HOLD for a value of this
 * magnitude, written in its shortest accepted form (leading dot below 1):
 * `.3333` for 1/3, `4.333` for 13/3, `13.33` for 40/3, `133.3` for 400/3.
 */
export function sprCapacityDecimals(value: number): number {
  const a = Math.abs(value)
  if (a < 1) return SPR_VALUE_CHARS - 1 // ".3333" — the dot costs one char
  const intDigits = Math.floor(a).toFixed(0).length
  return Math.max(0, SPR_VALUE_CHARS - intDigits - 1)
}

/**
 * Decimal places a repeating answer must carry to be accepted: three when the
 * grid has room for three, otherwise as many as fit.
 *
 * Why this is not a flat 3 (the bug this replaces): the old rule asked for
 * >= 3 decimal places whatever the answer's size, but the grid holds five
 * characters. For 40/3 the most accurate entry that FITS is `13.33` — two
 * places — and `13.333` is six characters, which the input box refuses to
 * accept in the first place. The flat rule therefore made every repeating
 * answer >= 10 impossible to enter as a decimal at all. College Board's rule
 * is "enter the most accurate value the answer field will accommodate", which
 * is what this computes; we stay deliberately lenient below 1, where `.333`
 * is accepted even though `.3333` would also fit.
 */
export function sprRequiredDecimals(value: number): number {
  return Math.min(3, sprCapacityDecimals(value))
}

/**
 * Grade an SPR answer the way the Digital SAT does: every equivalent numeric
 * form of the correct answer is accepted. So `3/5`, `6/10`, `0.6`, and `.6`
 * all match a stored answer in any of those forms. For non-terminating values,
 * a decimal entry carrying at least `sprRequiredDecimals(key)` decimal places
 * is accepted if it is the true value TRUNCATED or ROUNDED to that many places
 * (e.g. `.333` for 1/3; `.666` or `.667` for 2/3; `13.33` for 40/3, where
 * nothing longer fits the grid) — and only then, so a genuine near-miss like
 * `0.599` for 0.6 is still wrong.
 */
export function gradeSpr(
  studentRaw: string | null | undefined,
  storedRaw: string | null | undefined,
): boolean {
  const sStr = (studentRaw ?? '').trim()
  const kStr = (storedRaw ?? '').trim()
  if (sStr === '' || kStr === '') return false
  if (sStr === kStr) return true
  const s = parseSprNumber(sStr)
  const k = parseSprNumber(kStr)
  if (s === null || k === null) return sStr === kStr // non-numeric: exact only
  // Exact numeric equality (handles 3/5 == 0.6 == 6/10 == .60 == 1.8 == 1.80).
  if (Math.abs(s - k) <= 1e-9 * Math.max(1, Math.abs(k))) return true
  // SAT truncate-or-round rule for non-terminating answers.
  const dp = sprDecimalPlaces(sStr)
  if (dp >= sprRequiredDecimals(k)) {
    const f = Math.pow(10, dp)
    const trunc = Math.trunc(k * f) / f
    const round = Math.round(k * f) / f
    if (Math.abs(s - trunc) <= 1e-9 || Math.abs(s - round) <= 1e-9) return true
  }
  return false
}

/**
 * Grade one submitted answer against its key, for the surfaces that carry a
 * single free-text `answer` plus the question's `itemType` (practice, drills,
 * rapid fire, adaptive, review). SPR routes to `gradeSpr`; everything else is
 * an MCQ label compare, trimmed and case-insensitive.
 *
 * This is the entry point those routes MUST call — it is what stops a sixth
 * surface from reinventing `a.trim() === b.trim()` and reintroducing the bug.
 */
export function gradeSubmittedAnswer(
  answerKey: string | null | undefined,
  itemType: string | null | undefined,
  submitted: string | null | undefined,
): boolean {
  if (answerKey == null || submitted == null) return false
  if (itemType === 'SPR') return gradeSpr(submitted, answerKey)
  const a = submitted.trim()
  if (a === '') return false
  return a.toUpperCase() === answerKey.trim().toUpperCase()
}

/**
 * Back-compat alias for the lesson MiniCheck. Same grader as everything else
 * now — previously this was the weaker of the two implementations.
 */
export function compareSprAnswer(student: string, canonical: string): boolean {
  return gradeSpr(student, canonical)
}

/** Back-compat alias for `parseSprNumber`. */
export function normalizeSpr(s: string): number | null {
  return parseSprNumber(s)
}

/**
 * A wrong SPR entry that is the RIGHT value rounded or truncated too early —
 * `4.33` for 13/3, when the grid had room for `4.333`.
 *
 * Feedback only: this never affects grading, and it is deliberately narrow.
 * It fires only when the entry is exactly the key truncated or rounded at the
 * places it carried, so an ordinary wrong answer that happens to be nearby
 * (`4.35`) is not dressed up as a formatting slip.
 */
export type SprPrecisionMiss = {
  /** Decimal places the entry carried. */
  entered: number
  /** Decimal places the grid had room for. */
  required: number
  /** An entry that would have been accepted, e.g. `4.333`. */
  suggestion: string
}

export function describeSprPrecisionMiss(
  studentRaw: string | null | undefined,
  storedRaw: string | null | undefined,
): SprPrecisionMiss | null {
  const sStr = (studentRaw ?? '').trim()
  if (sStr === '' || gradeSpr(sStr, storedRaw)) return null
  const s = parseSprNumber(sStr)
  const k = parseSprNumber(storedRaw)
  if (s === null || k === null) return null
  const entered = sprDecimalPlaces(sStr)
  const required = sprRequiredDecimals(k)
  // `entered < required` needs no check: an entry carrying enough places AND
  // matching the truncation below is exactly what `gradeSpr` accepts, and it
  // already said no.
  if (entered < 1) return null
  const f = Math.pow(10, entered)
  const trunc = Math.trunc(k * f) / f
  const round = Math.round(k * f) / f
  if (Math.abs(s - trunc) > 1e-9 && Math.abs(s - round) > 1e-9) return null
  const g = Math.pow(10, required)
  return {
    entered,
    required,
    suggestion: (Math.trunc(k * g) / g).toFixed(required),
  }
}

// ---------------------------------------------------------------------------
// Entry rules — what may be SUBMITTED, before anything is graded.
//
// Moved here from src/lib/sat/answer-validation.ts so the mobile grid-in
// blocks exactly the entries the server rejects. An unreadable entry ("abc",
// "1,000", a 7-character decimal) is an input error, never a wrong answer: the
// web practice surfaces once recorded a miss — accuracy, mastery, a review
// card — for what was a typo.
// ---------------------------------------------------------------------------

// College Board SPR (student-produced-response) entry rules: up to 5
// characters for a positive answer, up to 6 including the leading "-" for a
// negative answer. We cap length here so a paste / fuzzing can't store a giant
// string, while still accepting every legitimate grid-in form.
const SPR_MAX_LEN_POSITIVE = 5
const SPR_MAX_LEN_NEGATIVE = 6

/** The longest entry the answer box takes: 5 characters, 6 with a leading minus. */
export function sprMaxLength(entry: string): number {
  return entry.trim().startsWith('-') ? SPR_MAX_LEN_NEGATIVE : SPR_MAX_LEN_POSITIVE
}

/**
 * Validate an SPR numeric entry. Rejects non-strings, empty / whitespace-only
 * input, over-length input (per the CB caps above), and anything the grader
 * can't parse as a number / decimal / fraction.
 */
export function validateNumericAnswer(
  numericAnswer: unknown,
): { ok: true } | { ok: false; error: string } {
  if (typeof numericAnswer !== 'string')
    return { ok: false, error: 'numericAnswer must be a string' }
  const t = numericAnswer.trim()
  if (t === '') return { ok: false, error: 'empty numeric answer' }
  if (t.length > sprMaxLength(t)) return { ok: false, error: 'numeric answer too long' }
  if (parseSprNumber(t) === null) return { ok: false, error: 'numeric answer not a valid number' }
  return { ok: true }
}

/** Shown when a grid-in entry isn't a number the grader can read. */
export const SPR_FORMAT_MESSAGE =
  'Not a recognized number — use a whole number, fraction (e.g. 3/4), or decimal (e.g. .75).'
/** Shown when a grid-in entry won't fit the answer box (the SAT's 5/6-char cap). */
export const SPR_LENGTH_MESSAGE =
  'Too long — a grid-in answer fits in 5 characters (6 with a minus sign).'
/** Shown when a grid-in answer is submitted blank. */
export const SPR_EMPTY_MESSAGE = 'Enter an answer, or choose "I don\'t know".'

/**
 * The student-facing problem with a grid-in entry, or null when the grader can
 * read it. A thin wrapper over validateNumericAnswer — the one SPR format rule
 * — that maps its error to copy a student can act on.
 */
export function sprEntryProblem(raw: unknown): string | null {
  const v = validateNumericAnswer(raw)
  if (v.ok) return null
  if (v.error === 'empty numeric answer') return SPR_EMPTY_MESSAGE
  if (v.error === 'numeric answer too long') return SPR_LENGTH_MESSAGE
  return SPR_FORMAT_MESSAGE
}
